/**
 * Publications Section Component
 * Migrated to TypeScript with subcomponents
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaExternalLinkAlt, FaCalendarAlt, FaFileAlt, FaUser } from 'react-icons/fa';
import { publications } from '../../data/publications';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const Publications: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="publications" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                Publications
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Research papers and academic publications
            </p>
          </div>

          {/* Publications */}
          <div className="max-w-7xl mx-auto">
            {publications.map((pub, index) => (
              <div
                key={pub.id}
                className={`${colors.cardBackground} rounded-2xl p-8 shadow-lg border ${colors.borderColor} hover:shadow-xl transition-all duration-300 transform hover:scale-105`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {/* Header */}
                <div className="mb-6">
                  <h3 className={`text-3xl font-bold ${colors.textPrimary} mb-4`}>
                    {pub.title}
                  </h3>
                  
                  {/* Metadata with icons */}
                  <div className="flex flex-wrap items-center gap-6 text-sm mb-4">
                    <div className="flex items-center space-x-2">
                      <FaFileAlt className={`w-4 h-4 ${colors.textSecondary}`} />
                      <span className={colors.textSecondary}>
                        IJRTE, ISSN: 2277-3878, Volume-7 Issue-6, March 2019
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaCalendarAlt className={`w-4 h-4 ${colors.textSecondary}`} />
                      <span className={colors.textSecondary}>{pub.date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <FaUser className={`w-4 h-4 ${colors.textSecondary}`} />
                      <span className={colors.textSecondary}>
                        {Array.isArray(pub.authors) ? pub.authors.join(' & ') : pub.authors}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Abstract */}
                <div className="mb-6">
                  <h4 className={`text-lg font-semibold ${colors.textPrimary} mb-3`}>
                    Abstract
                  </h4>
                  <p className={`${colors.textSecondary} leading-relaxed text-justify`}>
                    We proposes a novel image denoising technique that combines spatial domain filters with a Block Matching Convolutional Neural Network (BMCNN). We evaluated various spatial domain filters (e.g., arithmetic mean, median) and the BMCNN separately, then combine them to tackle different types of image noise (Gaussian, salt and pepper, speckle, Poisson). The BMCNN incorporates hard thresholding, Wiener filtering, and Haar transform with a pre-trained CNN. Performance is measured using Peak Signal-to-Noise Ratio (PSNR). Results show that the combined approach generally outperforms either method used alone, especially for salt and pepper and speckle noise, while remaining computationally efficient. The study highlights the importance of choosing appropriate spatial domain filters to optimize overall denoising performance when combined with BMCNN.
                  </p>
                </div>

                {/* Read Full Paper Button */}
                <div className="flex justify-end">
                  <a
                    href="https://www.ijrte.org/wp-content/uploads/papers/v7i6/F2810037619.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors"
                  >
                    <FaExternalLinkAlt className="w-4 h-4" />
                    <span>Read Full Paper</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Publications;

